import type { CellContext, ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useSnapshot } from 'valtio';
import { DigitizationType, type OrganizationDetail, type Project } from '~/common/types';
import Checkbox from '~/components2/forms/Checkbox';
import { AUTO_COL_WIDTH } from '~/components2/Table';
import store from '../../../store';

const checkboxCell = ({ row }: CellContext<any, unknown>) => {
  const { agency } = useSnapshot(store) as typeof store;
  const id = row.original.id;

  const existingAccess = useMemo<Set<string>>(() => {
    const orgAndProjIds: Set<string> = new Set();

    agency?.digitization.forEach((d) => {
      if (d.type === DigitizationType.Project) {
        orgAndProjIds.add(d.project.id);
      } else {
        orgAndProjIds.add(d.organization.id);
      }
    });

    return orgAndProjIds;
  }, [agency?.digitization]);

  const isChecked = row.getIsSelected() || existingAccess.has(id);
  const isDisabled = existingAccess.has(id);

  return (
    <div className="flex w-full items-center justify-center">
      <Checkbox disabled={isDisabled} value={isChecked} checked={isChecked} label="" onChange={row.toggleSelected} />
    </div>
  );
};

export const projectColumns: ColumnDef<Project>[] = [
  {
    id: 'select',
    size: 25,
    cell: checkboxCell,
  },
  {
    id: 'projectId',
    header: 'Project ID',
    accessorKey: 'projectId',
    size: 100,
    cell: (cell) => {
      const id = cell.row.original.id;
      const value = cell.getValue<string>();
      const link = `${window.location.origin}/projects/${id}`;

      return (
        <a href={link} target="_blank">
          {value}
        </a>
      );
    },
  },
  {
    id: 'name',
    header: 'Project Name',
    accessorKey: 'name',
    size: 300,
  },
  {
    id: 'org',
    header: 'Organization',
    accessorFn: (p) => (p.organization as any)?.name || '',
  },
];

export const orgColumns: ColumnDef<OrganizationDetail>[] = [
  {
    id: 'select',
    size: 25,
    cell: checkboxCell,
  },
  {
    id: 'name',
    size: AUTO_COL_WIDTH,
    header: 'Organization Name',
    accessorKey: 'name',
  },
];
